// Best-effort preview frames for a selected video File — no upload, no conversion. Loads the file into
// a hidden <video> (streamed from an object URL, so no multi-GB copy) and grabs, at each of `segments`
// points evenly spread across the duration, a short burst of `framesPerSegment` frames over a
// `windowSec`-long window — so each segment plays back as a ~1s clip. Frames are reported via
// onFrame(flatIndex, { dataUrl, width, height, time }) as they're captured (flatIndex = segment*framesPerSegment + k),
// with orientation-corrected dimensions. Resolves { width, height, count } when done. Decodes nothing
// the browser can't play (HEVC without OS support, .osv) — those just yield no frames.

export function extractFrames(
  file,
  { segments = 5, framesPerSegment = 6, windowSec = 1, maxSize = 640, timeoutMs = 30000, rotation = 0 } = {},
  onFrame,
) {
  return new Promise((resolve) => {
    if (!file) { resolve({ width: 0, height: 0, count: 0 }); return; }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    video.playsInline = true;

    let settled = false;
    let times = [];
    let idx = 0;
    let captured = 0;
    let dims = { width: 0, height: 0 };

    const cleanup = () => {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      video.removeAttribute('src');
      try { video.load(); } catch { /* ignore */ }
    };
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve({ width: dims.width, height: dims.height, count: captured });
    };
    const timer = setTimeout(finish, timeoutMs);

    const captureFrame = () => {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return;
      try {
        const rot = ((rotation % 360) + 360) % 360;
        const quarter = rot === 90 || rot === 270;
        // File says rotated 90/270 but browser still reports landscape dims → it isn't applying the
        // rotation to this frame, so rotate the canvas ourselves (otherwise don't double-rotate).
        const needRotate = quarter && vw >= vh;

        const scale = Math.min(1, maxSize / Math.max(vw, vh));
        const sw = Math.max(1, Math.round(vw * scale));
        const sh = Math.max(1, Math.round(vh * scale));

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (needRotate) {
          canvas.width = sh;
          canvas.height = sw;
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((rot * Math.PI) / 180);
          ctx.drawImage(video, -sw / 2, -sh / 2, sw, sh);
        } else {
          canvas.width = sw;
          canvas.height = sh;
          ctx.drawImage(video, 0, 0, sw, sh);
        }

        const dispW = needRotate ? vh : vw;
        const dispH = needRotate ? vw : vh;
        if (!dims.width) dims = { width: dispW, height: dispH };
        captured++;
        if (onFrame) {
          try {
            onFrame(idx, { dataUrl: canvas.toDataURL('image/jpeg', 0.72), width: dispW, height: dispH, time: times[idx] });
          } catch { /* ignore callback errors */ }
        }
      } catch { /* skip this frame (tainted canvas / draw failure) */ }
    };

    const grabNext = () => {
      if (settled) return;
      if (idx >= times.length) { finish(); return; }
      const t = times[idx];
      // A target equal to the current position won't fire `seeked` (short videos / overlapping
      // windows) — capture the current frame directly and move on, keeping flatIndex alignment.
      if (idx > 0 && Math.abs(t - video.currentTime) < 0.01) {
        captureFrame();
        idx++;
        grabNext();
        return;
      }
      try { video.currentTime = t; } catch { finish(); }
    };

    video.onloadedmetadata = () => {
      const d = video.duration;
      const dur = isFinite(d) && d > 0 ? d : 0;
      const total = dur > 0 ? dur : windowSec * segments;
      const raw = [];
      for (let s = 0; s < segments; s++) {
        const center = total * (s + 0.5) / segments;
        let start = center - windowSec / 2;
        let end = center + windowSec / 2;
        if (start < 0) { start = 0; end = Math.min(total, windowSec); }
        if (end > total) { end = total; start = Math.max(0, total - windowSec); }
        for (let k = 0; k < framesPerSegment; k++) {
          const t = framesPerSegment > 1 ? start + (end - start) * k / (framesPerSegment - 1) : (start + end) / 2;
          raw.push(Math.max(0.03, Math.min(t, total - 0.02)));
        }
      }
      times = raw;
      grabNext();
    };

    video.onseeked = () => {
      if (settled) return;
      captureFrame();
      idx++;
      grabNext();
    };

    video.onerror = finish;
    video.src = url;
    try { video.load(); } catch { /* ignore */ }
  });
}
